(() => {
  if (window.canvoilFrameClarityLoaded) return;
  window.canvoilFrameClarityLoaded = true;

  const instances = new Map();
  const framed = new Set(['Gold Frame', 'Silver Frame', 'Black Frame', 'White Frame', 'Walnut Wood', 'Natural Wood', 'Dark Wood']);
  function copyFor(value) {
    if (value === 'Rolled Canvas') return ['Not ready to hang', 'Choose stretched or framed.'];
    if (value === 'Unframed Stretched Canvas') return ['Ready to hang', 'No outer frame.'];
    if (framed.has(value)) return ['Ready to hang', 'Outer frame included.'];
    return null;
  }

  function previewConfigFor(section) {
    const script = section.querySelector('script[data-canvoil-frame-preview]');
    const productId = section.querySelector('.product[data-product-id]')?.dataset.productId || script?.dataset.productId;
    if (!script || !['15422024188183', '15394294989079'].includes(productId) || script.dataset.productId !== productId) return null;
    try {
      const config = JSON.parse(script.textContent);
      return typeof config.modalId === 'string' && config.modalId && config.images && typeof config.images === 'object' ? config : null;
    } catch (_) {
      return null;
    }
  }

  function setup(section) {
    if (instances.has(section)) return;
    let note;
    let noteTitle;
    let noteBody;
    let scheduled;
    const noteId = `${section.id}-frame-note`;
    const previewConfig = previewConfigFor(section);
    const failedThumbnails = new Set();
    let previewButton;
    let previewThumbnail;
    let previewValue;
    let previewModal;
    let previewImage;
    let previewTitle;
    let previewError;
    let onImageLoad;
    let onImageError;

    function getPreviewModal() {
      if (!previewConfig) return null;
      const modal = document.getElementById(previewConfig.modalId);
      if (!modal || typeof modal.show !== 'function') return null;
      if (previewModal === modal) return modal;
      const image = modal.querySelector('[data-frame-preview-image]');
      const title = modal.querySelector('[data-frame-preview-title]');
      const error = modal.querySelector('[data-frame-preview-error]');
      if (!image || !title || !error) return null;
      previewImage?.removeEventListener('load', onImageLoad);
      previewImage?.removeEventListener('error', onImageError);
      previewModal = modal;
      previewImage = image;
      previewTitle = title;
      previewError = error;
      onImageLoad = () => { image.hidden = false; error.hidden = true; };
      onImageError = () => {
        image.hidden = true;
        if (!error.textContent.trim()) error.textContent = 'This preview could not load. Please try again.';
        error.hidden = false;
      };
      image.addEventListener('load', onImageLoad);
      image.addEventListener('error', onImageError);
      return modal;
    }

    function previewFor(value) {
      if (!copyFor(value) || failedThumbnails.has(value)) return null;
      const image = previewConfig?.images?.[value];
      if (!image || typeof image.thumb !== 'string' || !image.thumb.trim() || typeof image.full !== 'string' || !image.full.trim()) return null;
      return getPreviewModal() ? image : null;
    }

    function openPreview() {
      // Read the current checked control again; a pending GLO update must not
      // open the image belonging to the previous frame selection.
      if (scheduled != null) cancelAnimationFrame(scheduled);
      sync();
      const image = previewFor(previewValue);
      if (!image || !previewButton || previewButton.hidden) return;
      previewTitle.textContent = previewValue;
      previewImage.alt = `${previewConfig.productTitle || 'Artwork'} — ${previewValue}`;
      previewImage.width = Number.isInteger(image.width) && image.width > 0 ? image.width : 1254;
      previewImage.height = Number.isInteger(image.height) && image.height > 0 ? image.height : 1254;
      previewError.hidden = true;
      previewImage.hidden = false;
      // The full-size scene is requested only after an explicit preview click.
      if (previewImage.getAttribute('src') !== image.full || !previewImage.naturalWidth) previewImage.src = image.full;
      previewModal.show(previewButton);
      // The native focus trap attaches its Tab handling when an actual first
      // or last control receives focus; start on the visible close control.
      previewModal.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
    }

    function updatePreview(value, image) {
      previewValue = image ? value : null;
      note.classList.toggle('canvoil-frame-note--preview', Boolean(image));
      if (!image) {
        if (previewButton) previewButton.hidden = true;
        return;
      }
      if (!previewButton) {
        previewButton = document.createElement('button');
        previewButton.type = 'button';
        previewButton.className = 'canvoil-frame-preview__trigger';
        previewButton.setAttribute('aria-haspopup', 'dialog');
        previewButton.setAttribute('aria-controls', previewConfig.modalId);
        previewButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="6.5"/><path d="m15 15 5 5M7 10h6M10 7v6"/></svg>';
        previewButton.addEventListener('click', openPreview);
        note.append(previewButton);
      }
      previewButton.hidden = false;
      previewButton.setAttribute('aria-label', `View ${value} frame preview`);
      if (previewThumbnail?.dataset.frameValue === value && previewThumbnail.getAttribute('src') === image.thumb) return;
      const thumbnail = document.createElement('img');
      thumbnail.width = 44;
      thumbnail.height = 44;
      thumbnail.alt = '';
      thumbnail.loading = 'lazy';
      thumbnail.decoding = 'async';
      thumbnail.dataset.frameValue = value;
      thumbnail.addEventListener('error', () => {
        if (previewThumbnail !== thumbnail) return;
        failedThumbnails.add(value);
        schedule();
      }, {once: true});
      previewThumbnail?.remove();
      previewThumbnail = thumbnail;
      previewButton.prepend(thumbnail);
      thumbnail.src = image.thumb;
    }

    function sync() {
      scheduled = null;
      const heading = section.querySelector('.globo-swatch-product-detail .name-option[data-name="Frame" i]');
      const globoList = heading?.nextElementSibling;
      const nativeControl = section.querySelector('.canvoil-options input[name="options[Frame]" i]');
      const nativeGroup = nativeControl?.closest('[data-option-group]');
      const list = globoList?.matches('ul.g-variant-color-detail') ? globoList : nativeGroup?.querySelector('.canvoil-options__cards');
      const selected = list?.querySelector('input[type="radio"]:checked');
      const copy = copyFor(selected?.value);
      nativeGroup?.classList.add('canvoil-frame-group');

      if (list) {
        list.querySelectorAll('input[type="radio"]').forEach(input => {
          const label = input.nextElementSibling;
          if (!label?.matches('label') || label.htmlFor !== input.id) return;
          label.classList.add('canvoil-frame-choice');
          if (!label.querySelector('.canvoil-frame-check')) {
            const check = document.createElement('span');
            check.className = 'canvoil-frame-check';
            check.setAttribute('aria-hidden', 'true');
            check.innerHTML = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="m2 6 2.5 2.5L10 3" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            label.append(check);
          }
          // Preserve any descriptions supplied by the theme or app.
          const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
          const next = descriptions.filter(id => id !== noteId);
          if (input === selected && copy) next.push(noteId);
          if (next.length) input.setAttribute('aria-describedby', next.join(' '));
          else input.removeAttribute('aria-describedby');
        });
      }
      if (!copy) {
        previewValue = null;
        if (note) {
          updatePreview(null, null);
          note.hidden = true;
        }
        return;
      }
      if (!note) {
        note = document.createElement('p');
        note.id = noteId;
        note.className = 'canvoil-frame-note';
        note.setAttribute('role', 'status');
        note.setAttribute('aria-live', 'polite');
        note.setAttribute('aria-atomic', 'true');
        noteTitle = document.createElement('strong');
        noteBody = document.createElement('span');
        note.append(noteTitle, noteBody);
      }
      const preview = previewFor(selected.value);
      const title = preview && framed.has(selected.value) ? 'Ready to hang' : `${selected.value === 'Rolled Canvas' ? 'Canvas only' : selected.value === 'Unframed Stretched Canvas' ? 'Stretched Canvas' : selected.value} · ${copy[0]}`;
      if (noteTitle.textContent !== title) noteTitle.textContent = title;
      if (noteBody.textContent !== copy[1]) noteBody.textContent = copy[1];
      updatePreview(selected.value, preview);
      note.hidden = false;
      if (list.nextElementSibling !== note) list.after(note);
    }

    function schedule() {
      if (!instances.has(section)) return;
      if (scheduled == null) scheduled = requestAnimationFrame(sync);
    }
    function disconnect() {
      if (scheduled != null) cancelAnimationFrame(scheduled);
      section.removeEventListener('change', schedule);
      previewButton?.removeEventListener('click', openPreview);
      previewImage?.removeEventListener('load', onImageLoad);
      previewImage?.removeEventListener('error', onImageError);
      const modal = previewModal || (previewConfig && document.getElementById(previewConfig.modalId));
      if (modal?.hasAttribute('open') && typeof modal.hide === 'function') modal.hide();
      modal?.remove();
      note?.remove();
      instances.delete(section);
    }
    section.addEventListener('change', schedule);
    instances.set(section, {sync, schedule, disconnect});
    sync();
    if (previewConfig && !customElements.get('modal-dialog')) {
      customElements.whenDefined('modal-dialog').then(() => {
        if (section.isConnected && instances.has(section)) schedule();
      });
    }
  }

  function initialize() {
    instances.forEach((instance, section) => { if (!section.isConnected) instance.disconnect(); });
    document.querySelectorAll('section[id^="MainProduct-"]').forEach(section => {
      if (!section.closest('quick-view, quick-add-modal, modal-dialog')) setup(section);
    });
    instances.forEach(instance => instance.schedule());
  }
  // GLO announces both initial rendering and every option-widget rebuild.
  window.addEventListener('Globo.swatch.loaded', initialize);
  window.addEventListener('globoSwatchClickedVariantSwatch', initialize);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, section) => {
      if (section.dataset.section === event.detail?.sectionId || event.target.contains?.(section)) instance.disconnect();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
})();
